import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api';
import { Search, RefreshCw, AlertTriangle, CheckCircle2, XCircle, Clock, X, Pencil } from 'lucide-react';
import { can } from '../permissions';
import toast from 'react-hot-toast';
import Card from '../components/StatTile';

const money = n => 'AED ' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const day = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const daysSince = d => d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null;

const OUTCOME = {
  delivered: { label: 'Delivered', bg: '#dcfce7', fg: '#15803d', Icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', bg: '#fee2e2', fg: '#b91c1c', Icon: XCircle },
  unresolved:{ label: 'Open',      bg: '#fef3c7', fg: '#a16207', Icon: Clock },
};

export default function InvoiceTracking() {
  const [searchParams] = useSearchParams();
  const today = new Date().toISOString().split('T')[0];
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

  // pre-filled when arriving from a link (e.g. Management Report's customer
  // breakdown) -- falls back to the normal defaults otherwise
  const [from, setFrom] = useState(searchParams.get('from') || monthAgo);
  const [to, setTo] = useState(searchParams.get('to') || today);
  const [outcome, setOutcome] = useState('all');
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [data, setData] = useState({ summary: {}, invoices: [] });
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);
  const [staleOnly, setStaleOnly] = useState(false);
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from, to, outcome });
      if (q.trim()) params.set('q', q.trim());
      const { data } = await api.get('/api/tracking/invoices?' + params);
      setData(data);
    } catch (e) { /* keep the last good view */ }
    setLoading(false);
  }, [from, to, outcome, q]);

  useEffect(() => { load(); }, [from, to, outcome]);   // eslint-disable-line

  const openHistory = async (row) => {
    setHistory({ row, attempts: null });
    try {
      const { data } = await api.get(`/api/tracking/invoices/${row.root_id}/history`);
      setHistory({ row, attempts: data });
    } catch (e) { setHistory({ row, attempts: [] }); }
  };

  const canEdit = can('driver_reports', 'edit');
  const s = data.summary || {};
  // "Stale" means still open and first attempted more than a week ago — the
  // ones that have genuinely fallen through, as opposed to today's work.
  const visible = (data.invoices || []).filter(r =>
    !staleOnly || (r.outcome === 'unresolved' && (daysSince(r.first_attempt_date) || 0) > 7));

  if (!can('tracking', 'view')) {
    return <div style={{ padding: 24 }}>You don't have access to this page.</div>;
  }

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>Invoice tracking</h1>
      <p style={{ color: '#64748b', fontSize: 14, margin: '4px 0 20px' }}>
        Every invoice ends delivered or cancelled. Anything still open is listed first.
      </p>

      {/* filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inp} />
        <span style={{ color: '#94a3b8' }}>to</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inp} />
        <select value={outcome} onChange={e => setOutcome(e.target.value)} style={inp}>
          <option value="all">All outcomes</option>
          <option value="unresolved">Open only</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 14,
                        color: '#475569', cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={staleOnly}
                 onChange={e => { setStaleOnly(e.target.checked); if (e.target.checked) setOutcome('unresolved'); }}
                 style={{ width: 15, height: 15 }} />
          Open more than 7 days
        </label>
        <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
          <Search size={15} style={{ position: 'absolute', left: 11, top: 11, color: '#94a3b8' }} />
          <input value={q} onChange={e => setQ(e.target.value)}
                 onKeyDown={e => e.key === 'Enter' && load()}
                 placeholder="Invoice number or customer"
                 style={{ ...inp, paddingLeft: 33, width: '100%' }} />
        </div>
        <button onClick={load} style={btn}><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12, marginBottom: 22 }}>
        <Card label="Invoices" value={s.total || 0} sub={`${s.completion_rate || 0}% delivered`} />
        <Card icon={CheckCircle2} label="Delivered" value={s.delivered || 0} sub={money(s.delivered_value)} color="#15803d" />
        <Card icon={XCircle} label="Cancelled" value={s.cancelled || 0} sub={money(s.cancelled_value)} color="#b91c1c" />
        <Card icon={Clock} label="Still open" value={s.unresolved || 0} sub={money(s.unresolved_value)}
              color={s.unresolved ? '#a16207' : '#64748b'}
              highlight={!!s.unresolved} />
      </div>

      {s.unresolved > 0 && outcome === 'all' && (
        <div onClick={() => setOutcome('unresolved')}
             style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fffbeb',
                      border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px',
                      marginBottom: 18, cursor: 'pointer', fontSize: 14, color: '#92400e' }}>
          <AlertTriangle size={17} />
          <span><b>{s.unresolved} invoice{s.unresolved === 1 ? '' : 's'} without an ending.</b> Click to see only those.</span>
        </div>
      )}

      {/* table */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? <div style={empty}>Loading…</div>
         : !visible.length ? <div style={empty}>Nothing matches those filters.</div>
         : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Posting date', 'Invoice no.', 'Customer', 'Salesperson', 'Value', 'Driver', 'First attempt', 'Attempts', 'Outcome'].map(h =>
                    <th key={h} style={th}>{h}</th>)}
                  {canEdit && <th style={th}></th>}
                </tr>
              </thead>
              <tbody>
                {visible.map(r => {
                  const o = OUTCOME[r.outcome];
                  const open = r.outcome === 'unresolved';
                  const age = daysSince(r.first_attempt_date);
                  return (
                    <tr key={r.root_id} onClick={() => openHistory(r)}
                        style={{ cursor: 'pointer', background: open ? '#fffdf5' : '#fff' }}>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{day(r.posting_date)}</td>
                      <td style={{ ...td, fontFamily: 'monospace', fontWeight: 600 }}>
                        {r.invoice_no || <span style={{ color: '#dc2626' }}>missing</span>}
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 500 }}>{r.customer_name}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{r.customer_code}</div>
                      </td>
                      <td style={{ ...td, fontFamily: 'monospace', fontSize: 13 }}>{r.salesman_name || '—'}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {money(r.invoice_value)}
                      </td>
                      <td style={td}>{r.driver_name || '—'}</td>
                      <td style={td}>
                        {day(r.first_attempt_date)}
                        {open && age > 3 && (
                          <div style={{ fontSize: 12, color: '#b45309' }}>{age} days ago</div>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        {r.attempts > 1
                          ? <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '2px 8px',
                                           borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{r.attempts}</span>
                          : <span style={{ color: '#cbd5e1' }}>1</span>}
                      </td>
                      <td style={td}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                                       background: o.bg, color: o.fg, padding: '3px 10px',
                                       borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                          <o.Icon size={13} />{o.label}
                        </span>
                        {r.outcome === 'cancelled' && r.cancel_reason && (
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>{r.cancel_reason}</div>
                        )}
                        {open && r.reschedule_date && (
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>
                            due {day(r.reschedule_date)}
                          </div>
                        )}
                      </td>
                      {canEdit && (
                        <td style={{ ...td, textAlign: 'right' }}>
                          {open && (
                            <button onClick={e => { e.stopPropagation(); setActing(r); }}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                                       padding: '6px 11px', background: '#fff', border: '1px solid #cbd5e1',
                                       borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                                       color: '#0f172a' }}>
                              <Pencil size={12} /> Resolve
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {history && <HistoryModal data={history} onClose={() => setHistory(null)} />}
      {acting && <ResolveModal row={acting} onClose={() => setActing(null)} onDone={() => { setActing(null); load(); }} />}
    </div>
  );
}

function HistoryModal({ data, onClose }) {
  const { row, attempts } = data;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)',
         display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
           style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 620,
                    maxHeight: '85vh', overflow: 'auto' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: 17, fontWeight: 700 }}>
              {row.invoice_no || 'No invoice number'}
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
              {row.customer_name} · {row.customer_code} · {money(row.invoice_value)}
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, fontFamily: 'monospace' }}>
              Posted {day(row.posting_date)} · Salesperson {row.salesman_name || '—'}
              {row.latitude && row.longitude &&
                ` · ${Number(row.latitude).toFixed(5)}, ${Number(row.longitude).toFixed(5)}`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 0, cursor: 'pointer', color: '#94a3b8' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {!attempts ? <div style={{ color: '#94a3b8' }}>Loading history…</div>
           : !attempts.length ? <div style={{ color: '#94a3b8' }}>No history found.</div>
           : attempts.map((a, i) => {
              const done = a.status === 'delivered';
              const cancelled = a.delivery_status === 'cancelled';
              const resched = a.delivery_status === 'rescheduled' || (!done && !cancelled && a.reschedule_date);
              const dot = done ? '#22c55e' : cancelled ? '#ef4444' : resched ? '#f59e0b' : '#cbd5e1';
              return (
                <div key={a.id} style={{ display: 'flex', gap: 14, paddingBottom: i === attempts.length - 1 ? 0 : 18 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ width: 11, height: 11, borderRadius: '50%', background: dot, flex: 'none', marginTop: 5 }} />
                    {i < attempts.length - 1 &&
                      <div style={{ width: 2, flex: 1, background: '#e2e8f0', marginTop: 4 }} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      Attempt {a.attempt} · {day(a.session_date)}
                      {a.driver_name && <span style={{ color: '#64748b', fontWeight: 400 }}> · {a.driver_name}</span>}
                    </div>
                    <div style={{ fontSize: 13, color: '#475569', marginTop: 3 }}>
                      {done ? 'Delivered'
                        : cancelled ? `Cancelled${a.cancel_reason ? ' — ' + a.cancel_reason : ''}`
                        : resched ? `Rescheduled${a.reschedule_date ? ' to ' + day(a.reschedule_date) : ''}${a.cancel_reason ? ' — ' + a.cancel_reason : ''}`
                        : 'Still pending'}
                    </div>
                    {a.location_distance != null && (
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                        {a.location_distance} m from the registered location
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}


function ResolveModal({ row, onClose, onDone }) {
  const [choice, setChoice] = useState('');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(new Date(Date.now() + 86400000).toISOString().split('T')[0]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!choice) return;
    if (choice === 'cancelled' && !reason.trim()) {
      toast.error('Give a reason for cancelling'); return;
    }
    setBusy(true);
    try {
      await api.post(`/deliveries/admin-change-status/${row.current_id}`, {
        new_status: choice,
        reason: reason.trim() || null,
        reschedule_date: choice === 'rescheduled' ? date : null,
      });
      toast.success(
        choice === 'delivered' ? 'Marked delivered'
        : choice === 'cancelled' ? 'Cancelled'
        : 'Rescheduled');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not update the invoice');
      setBusy(false);
    }
  };

  const options = [
    { key: 'delivered',   title: 'Mark delivered',
      help: 'It was delivered but never closed off in the app.' },
    { key: 'rescheduled', title: 'Reschedule',
      help: 'Try again on another day. A new invoice is created for that date, keeping this invoice number.' },
    { key: 'cancelled',   title: 'Cancel',
      help: 'It will not be delivered. This ends the invoice.' },
  ];

  return (
    <div onClick={() => !busy && onClose()}
         style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
           style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 470, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Resolve this invoice</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.invoice_no || 'no number'}</span>
            {' · '}{row.customer_name}
          </div>
        </div>

        <div style={{ padding: '18px 24px' }}>
          {options.map(o => (
            <label key={o.key}
              style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '12px 14px',
                       border: '1px solid ' + (choice === o.key ? '#0f172a' : '#e2e8f0'),
                       background: choice === o.key ? '#f8fafc' : '#fff',
                       borderRadius: 10, marginBottom: 9, cursor: 'pointer' }}>
              <input type="radio" name="resolve" checked={choice === o.key}
                     onChange={() => setChoice(o.key)} style={{ marginTop: 3 }} />
              <span>
                <span style={{ fontWeight: 600, fontSize: 14.5, color: '#0f172a' }}>{o.title}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: '#64748b', marginTop: 2 }}>{o.help}</span>
              </span>
            </label>
          ))}

          {choice === 'rescheduled' && (
            <div style={{ marginTop: 12 }}>
              <label style={lbl}>New delivery date</label>
              <input type="date" value={date} min={new Date().toISOString().split('T')[0]}
                     onChange={e => setDate(e.target.value)} style={{ ...inp, width: '100%' }} />
            </div>
          )}

          {(choice === 'cancelled' || choice === 'rescheduled') && (
            <div style={{ marginTop: 12 }}>
              <label style={lbl}>{choice === 'cancelled' ? 'Reason' : 'Note (optional)'}</label>
              <input value={reason} onChange={e => setReason(e.target.value)}
                     placeholder={choice === 'cancelled' ? 'Customer refused, shop closed down…' : 'Shop closed, try Tuesday…'}
                     style={{ ...inp, width: '100%' }} />
            </div>
          )}
        </div>

        <div style={{ padding: '15px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc',
                      display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={busy}
                  style={{ padding: '9px 17px', background: '#fff', border: '1px solid #cbd5e1',
                           borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={submit} disabled={busy || !choice}
                  style={{ padding: '9px 17px', background: choice ? '#0f172a' : '#cbd5e1', color: '#fff',
                           border: 0, borderRadius: 8, fontSize: 14, fontWeight: 600,
                           cursor: choice ? 'pointer' : 'not-allowed' }}>
            {busy ? 'Saving…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 5 };

const inp = { padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0f172a' };
const btn = { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px', background: '#0f172a',
              color: '#fff', border: 0, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const th = { textAlign: 'left', fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase',
             color: '#64748b', padding: '11px 16px', background: '#f8fafc', fontWeight: 600 };
const td = { padding: '13px 16px', borderTop: '1px solid #f1f5f9', fontSize: 14, verticalAlign: 'top' };
const empty = { padding: 44, textAlign: 'center', color: '#94a3b8', fontSize: 14 };
